import { useCallback, useContext, useEffect } from 'react';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { LoaderContext } from '@contexts/loader/loader-context';
import { QueryFunction, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { RequestError } from '@utils/request/request.type';
import { showToastErrorRequest } from '@utils/show-toast-error-request/show-toast-error-request';

import { queryClient } from '../query-client';

import { useGetTokenQueryKey } from './auth/type';
import { useRequireAuthTokens } from './auth/use-get-tokens';
import { useTokenManagement } from './auth/use-token-management';
import { useToast } from './use-toast/use-toast';

export const useGenericQuery = <
  TQueryFnData = unknown,
  TError = RequestError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  key: TQueryKey,
  fn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn' | 'queryKey'>,
  handleLoading?: boolean,
  hideError?: boolean
): UseQueryResult<TData, TError> => {
  const { decrementLoaderQueue, incrementLoaderQueue } = useContext(LoaderContext);
  const { isSelectVenueActive, isPublicEventReportPage } = useContext(FeatureFlagContext);
  const toast = useToast();
  const requestId = `query-${key}`;

  const { getTokens } = useTokenManagement();

  const { accessToken, refreshToken } = getTokens();

  const { mutate: requireAuthTokens } = useRequireAuthTokens({
    refreshToken: refreshToken ?? '',
  });

  const handleTokens = useCallback(() => {
    requireAuthTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  useEffect(() => {
    const isMutatingRefreshTokenQuantity = queryClient.isMutating({ mutationKey: [useGetTokenQueryKey], exact: true });
    if (!accessToken && refreshToken && isMutatingRefreshTokenQuantity === 0) {
      handleTokens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, refreshToken]);

  const query = useQuery(key, fn, {
    ...options,
    enabled: (!!accessToken || isPublicEventReportPage) && options?.enabled,
    onSuccess: (data) => {
      if (options?.onSuccess) options.onSuccess(data);
      decrementLoaderQueue(requestId);
    },
    onError: (error) => {
      if (options?.onError) options.onError(error);
      if (error instanceof RequestError) {
        if (!hideError && showToastErrorRequest(error)) {
          toast.push({
            title: error.message,
            text: '',
            type: 'error',
            disableTimeOut: true,
          });
        }
      }
      decrementLoaderQueue(requestId);
    },
    onSettled: (data, error) => {
      if (options?.onSettled) options.onSettled(data, error);
      decrementLoaderQueue(requestId);
    },
    retry: (failureCount, error) => {
      if (isSelectVenueActive && error instanceof RequestError) {
        return false;
      }
      if (options?.retry !== false) {
        const numberOfRetry = options?.retry && typeof options?.retry === 'number' ? options?.retry : 4;
        return failureCount < numberOfRetry;
      }
      return false;
    },
  });

  useEffect(() => {
    if (query.isInitialLoading) {
      incrementLoaderQueue(requestId);
    }
    return () => {
      if (query.isInitialLoading) {
        decrementLoaderQueue(requestId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isInitialLoading, handleLoading]);

  return query;
};
